/*!
 * clint-phone.js — phone + extension handling for Miva
 * Arctic Leaf — Clint Pharmaceuticals
 *
 * THE RULE
 * --------
 * A break in the digits is the only trustworthy signal for where the number
 * ends and the extension starts. Stripping the string to digits first throws
 * that signal away, so we read it before stripping. No break, no split.
 *
 *   1. Skip a leading "1" (country code, put back afterwards) or a leading "0"
 *      (trunk-prefix junk, dropped). Neither counts toward the 7.
 *   2. Count 7 digits, ignoring separators as you pass them.
 *   3. After the 7th digit, keep going while you are still on digits. Stop at
 *      the first non-digit. Those digits are the phone number.
 *   4. Everything after that break, digits only, is the extension. Length is
 *      not judged on its own, but the value as a whole has to come in under 30
 *      characters. At 30 or over the extension is dropped and the number kept.
 *      This applies to PREFILL ONLY (see the two paths below).
 *   5. If there is no break at all, every digit goes in the phone field and the
 *      extension is left blank. Without a break there is no way to know which
 *      digits are the extension, so we do not guess.
 *   6. Under 7 digits total, there is no phone number here. Both fields blank.
 *
 * Worked examples:
 *     377-1301 x 223      7 digits, break, rest    -> 3771301 / 223
 *     19493771301.223     7 digits, run, break     -> 19493771301 / 223
 *     19493771301223      no break anywhere        -> 19493771301223 / (blank)
 *
 * There is no list of extension markers. "x 223", "X: 223", "ext. 223",
 * "ext no. 223", "x--223", ".223" and ") 223 (" are all just "a break, then
 * digits".
 *
 * Known and accepted: junk digits BEFORE the number shift the count.
 * "Suite 200, 949-377-1301" comes out as 200949377 / 1301. Nothing in the
 * string distinguishes the stray 200 from the real number.
 *
 * Storage format written back:
 *     19493771301 x 223       number with extension
 *     19493771301             number, no extension
 *     +442079460958 x 223     international
 *     (empty)                 nothing usable in the source value
 *
 * TWO PATHS, DELIBERATELY DIFFERENT
 * ---------------------------------
 * parse()  — reading a saved record. The data is someone else's and may be
 *            anything, so it is interpreted conservatively and the 30-character
 *            ceiling applies.
 * format() — the customer just typed these two fields. Strip every non-digit,
 *            join with " x ", submit. No length limit, no validation, no
 *            second-guessing. If the number is bad, Miva kicks it back and
 *            Miva's own error handling takes over.
 *
 * No dependencies. <script> tag (window.ClintPhone) or require().
 * parse(format(x)) always equals parse(x).
 */
(function (root, factory) {
	if (typeof module === 'object' && module.exports) module.exports = factory();
	else root.ClintPhone = factory();
})(typeof self !== 'undefined' ? self : this, function () {
	'use strict';

	const CONFIG = {
		extSeparator: ' x ', // sits between number and extension on save
		countryCode: '1', // NANP
		minDigits: 7, // below this there is no phone number
		localLength: 7, // legacy number with no area code
		fullLength: 10, // area code + number

		// PREFILL ONLY. When reading a saved record, if the value it would produce
		// is this long or longer, the extension is dropped and the phone number is
		// kept. Never applied to what a customer types — see format().
		maxTotalLength: 30,

		// The <input data-phone-display> the customer types into. Distinct from
		// maxTotalLength above — that one judges the combined stored value on
		// read; this one caps what goes in the display field itself. Values up
		// to this length are valid; anything longer fails validation with the
		// message below (the inputs carry no maxlength, so the overflow is
		// visible and flagged rather than silently blocked).
		displayMaxLength: 30,
		displayMaxLengthMessage: 'Phone number is limited to 30 characters',

		// Set true to force every domestic number to 11 digits (9493771301 ->
		// 19493771301). More uniform, but rewrites every existing 10-digit record.
		forceCountryCode: false,
	};

	function isDigit(character) {
		return character >= '0' && character <= '9';
	}
	function digitsOf(value) {
		return String(value == null ? '' : value).replace(/\D/g, '');
	}

	/* Fold unicode lookalikes to ASCII so one odd character in a NetSuite record
     cannot defeat the parser. Full-width digits become real digits. */
	function tidy(value) {
		let normalized = String(value == null ? '' : value);
		if (String.prototype.normalize) normalized = normalized.normalize('NFKC');
		return normalized.replace(/[×✕✖]/g, 'x').replace(/\s+/g, ' ').trim();
	}

	/* Is this run of non-digit characters a break between two different things,
     or just a separator inside one number?
     Separators inside a number are always a single character: "-", ".", " ",
     ")". So a gap is a real break if it is two or more characters long, or if
     it contains a letter. That covers " x ", " ext. ", "x", " / ", " - " while
     leaving "-", ".", " " and ")" alone. */
	function isStrongGap(gap) {
		return gap.length >= 2 || /[a-z]/i.test(gap);
	}

	/* Find the first real break that falls after at least `after` digits and
     still has digits following it. Returns { head, tail } or null. */
	function splitOnStrongGap(text, after) {
		let digitsSeen = 0;
		let index = 0;
		let gapStart;
		while (index < text.length) {
			if (isDigit(text.charAt(index))) {
				digitsSeen++;
				index++;
				continue;
			}
			gapStart = index;
			while (index < text.length && !isDigit(text.charAt(index))) index++;
			if (
				digitsSeen >= after &&
				index < text.length &&
				isStrongGap(text.slice(gapStart, index))
			) {
				return { head: text.slice(0, gapStart), tail: text.slice(index) };
			}
		}
		return null;
	}

	/* The value as it would be written to the server. Shared by format() and by
     the length check, so both always agree on what "the value" is. */
	function buildValue(phone, ext) {
		const tidiedPhone = tidy(phone);
		let plus = /^\+/.test(tidiedPhone);
		const phoneDigits = digitsOf(tidiedPhone);
		const extDigits = digitsOf(ext);
		if (!phoneDigits) return '';
		if (plus && phoneDigits.charAt(0) === CONFIG.countryCode) plus = false; // +1 is domestic
		return (plus ? '+' : '') + phoneDigits + (extDigits ? CONFIG.extSeparator + extDigits : '');
	}

	function tooLong(phone, ext) {
		return buildValue(phone, ext).length >= CONFIG.maxTotalLength;
	}

	/* ------------------------------------------------------------------ *
	 * parse(raw) -> { phone, ext }
	 * Never throws. Never returns null. Digit strings, except an
	 * international phone which keeps its leading "+".
	 * ------------------------------------------------------------------ */
	function parse(raw) {
		let work = tidy(raw);
		let intl = false;
		let allDigits;

		// International access prefixes are dialling instructions, not the number.
		if (/^\+/.test(work)) {
			work = work.replace(/^\+\s*/, '');
			intl = true;
		} else {
			allDigits = digitsOf(work);
			if (allDigits.length > 11 && allDigits.slice(0, 3) === '011') {
				work = work.replace(/^[^0-9]*011/, '');
				intl = true;
			} else if (
				allDigits.length > 10 &&
				allDigits.slice(0, 2) === '00' &&
				allDigits.charAt(2) !== '0'
			) {
				work = work.replace(/^[^0-9]*00/, '');
				intl = true;
			}
		}

		const digits = digitsOf(work);

		// Too short to be a phone number: blank, vanity (1-800-FLOWERS -> "1800"),
		// or free text ("n/a" -> "").
		if (digits.length < CONFIG.minDigits) return { phone: '', ext: '' };

		// Country code 1 is NANP by definition, so "+1..." falls through to the
		// domestic path. Any other country code: lengths vary per country, so the
		// only safe split is on an explicit break.
		if (intl && digits.charAt(0) !== CONFIG.countryCode) {
			const internationalSplit = splitOnStrongGap(work, CONFIG.minDigits);
			if (internationalSplit)
				return finish(
					'+' + digitsOf(internationalSplit.head),
					digitsOf(internationalSplit.tail),
				);
			return finish('+' + digits, '');
		}

		/* -- step 1: a leading 1 or 0 does not count toward the 7 ------------
       A leading "1" is the country code and goes back on the front. A leading
       "0" is trunk-prefix junk and is dropped. Both only apply when there is a
       full number behind them, so "0000000000" is left alone. */
		let lead = '';
		let skipFirst = false;
		if (digits.length >= CONFIG.fullLength + 1) {
			if (digits.charAt(0) === CONFIG.countryCode) {
				lead = CONFIG.countryCode;
				skipFirst = true;
			} else if (digits.charAt(0) === '0') {
				skipFirst = true;
			}
		}

		let start = 0;
		if (skipFirst) {
			while (start < work.length && !isDigit(work.charAt(start))) start++;
			start++;
		}

		/* -- steps 2 and 3: count 7 digits, then run to the first non-digit -- */
		let count = 0;
		let end = work.length;
		for (let index = start; index < work.length; index++) {
			if (!isDigit(work.charAt(index))) continue;
			if (++count < CONFIG.localLength) continue;
			let runEnd = index + 1;
			while (runEnd < work.length && isDigit(work.charAt(runEnd))) runEnd++;
			end = runEnd;
			break;
		}

		/* -- steps 4 and 5: whatever follows the break is the extension.
       If there was no break, end === work.length, so the tail is empty and
       every digit stays in the phone field. */
		return finish(
			lead + digitsOf(work.slice(start, end)),
			digitsOf(work.slice(end)),
		);
	}

	function finish(phone, ext) {
		let finalPhone = phone;
		let finalExt = ext;
		if (CONFIG.forceCountryCode && finalPhone.length === CONFIG.fullLength) {
			finalPhone = CONFIG.countryCode + finalPhone;
		}
		// Keep the number, lose the extension, rather than overflow the field.
		if (finalExt && tooLong(finalPhone, finalExt)) finalExt = '';
		return { phone: finalPhone, ext: finalExt };
	}

	/* ------------------------------------------------------------------ *
	 * format(phone, ext) -> the single value written to the server
	 * ------------------------------------------------------------------ */
	function format(phone, ext) {
		return buildValue(phone, ext);
	}

	/* Any messy value -> the uniform stored value. */
	function normalize(raw) {
		const parsed = parse(raw);
		return format(parsed.phone, parsed.ext);
	}

	/* Pretty version for the input box only. Storage stays digits. */
	function formatDisplay(phone) {
		const tidied = tidy(phone);
		if (/^\+/.test(tidied)) return '+' + digitsOf(tidied);
		const digits = digitsOf(tidied);
		if (digits.length === 11 && digits.charAt(0) === CONFIG.countryCode) {
			return '1 (' + digits.slice(1, 4) + ') ' + digits.slice(4, 7) + '-' + digits.slice(7);
		}
		if (digits.length === 10)
			return '(' + digits.slice(0, 3) + ') ' + digits.slice(3, 6) + '-' + digits.slice(6);
		if (digits.length === 7) return digits.slice(0, 3) + '-' + digits.slice(3);
		return digits;
	}

	return {
		parse,
		format,
		normalize,
		formatDisplay,
		digitsOf,
		CONFIG,
	};
});
